// src/App.jsx
// -----------------------------------------------------------------------------
// Top-level route definitions for the whole app.
//   - Public routes: /login, /forgot-password
//   - Protected routes: everything else, wrapped in <MainLayout> (sidebar +
//     header + footer) and guarded by <ProtectedRoute> so only logged-in
//     users can reach them. Admin-only pages pass allowedRoles.
// -----------------------------------------------------------------------------
import { Routes, Route, Navigate } from 'react-router-dom';

import ProtectedRoute from './routes/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import { ROLES } from './context/AuthContext';

// ---------- Public pages ----------
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import RegisterForm from './pages/Auth/RegisterForm';
import ChangePassword from './pages/Auth/ChangePassword';
import ResetPassword from './pages/Auth/ResetPassword';

// ---------- Protected pages ----------
import Dashboard from './pages/Dashboard/Dashboard';
import TicketList from './pages/Tickets/TicketList';
import TicketDetail from './pages/Tickets/TicketDetail';
import TicketStatusHistoryList from './pages/Tickets/TicketStatusHistoryList';
import TicketLinkTypeList from './pages/TicketLinkTypes/TicketLinkTypeList';
import TicketMandatoryFieldList from './pages/Tickets/TicketMandatoryFieldList';
import TicketReopenReasonList from './pages/Tickets/TicketReopenReasonList';
import TicketSeverityList from './pages/Tickets/TicketSeverityList';
import TicketStatusList from './pages/Tickets/TicketStatusList';
import ProjectList from './pages/Projects/ProjectList';
import ProjectDetail from './pages/Projects/ProjectDetail';
import ProjectPhaseList from './pages/Projects/ProjectPhaseList';
import ProjectEnvironmentList from './pages/Projects/ProjectEnvironmentList';
import ProjectTeamList from './pages/Projects/ProjectTeamList';
import ProjectTeamMemberList from './pages/Projects/ProjectTeamMemberList';
import UserList from './pages/Users/UserList';
import RoleList from './pages/Roles/RoleList';
import ClientOrgList from './pages/ClientOrganizations/ClientOrgList';
import PermissionList from './pages/Permissions/PermissionList';
import TicketTypeList from './pages/TicketTypes/TicketTypeList';
import NotificationList from './pages/Notifications/NotificationList';
import ClientQuestionList from './pages/ClientQuestions/ClientQuestionList';

export default function App() {
  return (
    <Routes>
      {/* ---------- Public routes ---------- */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/register" element={<RegisterForm />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* ---------- Protected routes (share the Sidebar/Header/Footer shell) ---------- */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Tickets - visible to every logged-in role */}
        <Route path="/tickets" element={<TicketList />} />
        <Route path="/tickets/:ticketId" element={<TicketDetail />} />
        <Route path="/ticket-status-history" element={<TicketStatusHistoryList />} />
        <Route path="/ticket-link-types" element={<TicketLinkTypeList />} />
        <Route
          path="/ticket-mandatory-fields"
          element={
            <ProtectedRoute>
              <TicketMandatoryFieldList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-reopen-reasons"
          element={
            <ProtectedRoute>
              <TicketReopenReasonList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-severities"
          element={
            <ProtectedRoute>
              <TicketSeverityList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-statuses"
          element={
            <ProtectedRoute>
              <TicketStatusList />
            </ProtectedRoute>
          }
        />

        {/* Projects - Admin / PM / Team Member */}
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <ProjectList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-phases"
          element={
            <ProtectedRoute>
              <ProjectPhaseList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-environments"
          element={
            <ProtectedRoute>
              <ProjectEnvironmentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-teams"
          element={
            <ProtectedRoute>
              <ProjectTeamList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project-team-members"
          element={
            <ProtectedRoute>
              <ProjectTeamMemberList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-questions"
          element={
            <ProtectedRoute>
              <ClientQuestionList />
            </ProtectedRoute>
          }
        />

        {/* Admin-only screens */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/roles"
          element={
            <ProtectedRoute>
              <RoleList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/permissions"
          element={
            <ProtectedRoute>
              <PermissionList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ticket-types"
          element={
            <ProtectedRoute>
              <TicketTypeList />
            </ProtectedRoute>
          }
        />

        {/* Admin / PM screen */}
        <Route
          path="/client-organizations"
          element={
            <ProtectedRoute>
              <ClientOrgList />
            </ProtectedRoute>
          }
        />

        {/* Notifications - everyone */}
        <Route path="/notifications" element={<NotificationList />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Route>

      {/* Catch-all -> dashboard (or login, if not authenticated - ProtectedRoute handles that) */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
